/**
 * Toast notification system
 * Provides global toast notifications for success, error, warning, and info messages
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  message: string
}

type Listener = (toasts: Toast[]) => void

let toasts: Toast[] = []
const listeners: Set<Listener> = new Set()

function notify(): void {
  listeners.forEach((listener) => listener([...toasts]))
}

function addToast(type: ToastType, message: string): void {
  const id = crypto.randomUUID()
  toasts = [...toasts, { id, type, message }]
  notify()
  setTimeout(() => removeToast(id), 5000)
}

function removeToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id)
  notify()
}

export const toast = {
  success: (message: string): void => addToast('success', message),
  error: (message: string): void => addToast('error', message),
  warning: (message: string): void => addToast('warning', message),
  info: (message: string): void => addToast('info', message),
  dismiss: (id: string): void => removeToast(id),
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener)
    listener([...toasts])
    return () => listeners.delete(listener)
  },
}
